import type { MediaAsset } from './blob-service';

interface LoadOptions {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
}

const DEFAULT_OPTIONS: LoadOptions = {
  timeout: 5000,    // 5 second timeout
  retries: 2,       // 2 retries
  retryDelay: 1000, // 1 second delay between retries
};

export async function loadMediaWithFallback(
  asset: MediaAsset,
  options: LoadOptions = DEFAULT_OPTIONS
): Promise<Response> {
  let attempts = 0;
  
  while (attempts <= (options.retries ?? DEFAULT_OPTIONS.retries!)) {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      options.timeout ?? DEFAULT_OPTIONS.timeout
    );

    try {
      // Try blob URL first if available
      if (asset.blobUrl) {
        const response = await fetch(asset.blobUrl, {
          signal: controller.signal
        });
        
        if (response.ok) {
          clearTimeout(timeoutId);
          return response;
        }
      }

      // If blob URL fails or isn't available, try fallback URL
      const fallbackResponse = await fetch(asset.fallbackUrl, {
        signal: controller.signal
      });

      if (fallbackResponse.ok) {
        clearTimeout(timeoutId);
        return fallbackResponse;
      }

      throw new Error('Both blob and fallback URLs failed to load');
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn(`Request timeout for ${asset.id} (attempt ${attempts + 1})`);
      } else {
        console.error(`Failed to load ${asset.id} (attempt ${attempts + 1}):`, error);
      }

      attempts++;
      
      // If we have more retries, wait before trying again
      if (attempts <= (options.retries ?? DEFAULT_OPTIONS.retries!)) {
        await new Promise(resolve => 
          setTimeout(resolve, options.retryDelay ?? DEFAULT_OPTIONS.retryDelay)
        );
      }
    }
  }

  throw new Error(`Failed to load media after ${attempts} attempts`);
}

export function getMediaLoadingState(asset: MediaAsset): 'loading' | 'ready' | 'error' {
  if (!asset.blobUrl && !asset.fallbackUrl) {
    return 'error';
  }
  
  // Add more sophisticated checks here if needed
  return 'ready';
}

export async function preloadMedia(assets: MediaAsset[]): Promise<void> {
  // Preload media in parallel with a concurrency limit
  const CONCURRENCY_LIMIT = 3;
  const queue = [...assets];
  const loading = new Set<Promise<void>>();

  while (queue.length > 0 || loading.size > 0) {
    // Fill up loading set to concurrency limit
    while (queue.length > 0 && loading.size < CONCURRENCY_LIMIT) {
      const asset = queue.shift()!;
      const loadPromise = loadMediaWithFallback(asset)
        .then(() => {
          loading.delete(loadPromise);
          return;
        })
        .catch(() => {
          loading.delete(loadPromise);
          return;
        });
      loading.add(loadPromise);
    }

    // Wait for at least one promise to complete
    if (loading.size > 0) {
      await Promise.race(loading);
    }
  }
} 